import type { Segment } from './segments';

/**
 * Converts an SVG elliptical arc to cubic Bézier segments using the endpoint-to-center
 * conversion in SVG 1.1 Implementation Notes F.6.5–F.6.6, split into pieces of at most
 * 90°. The maximum radial error of a 90° cubic approximation is about 2.7e-4 × radius.
 *
 * Returns an empty array when the endpoints coincide (the arc is omitted) and a single
 * line when either radius is zero, matching SVG's out-of-range parameter rules.
 */
export function arcToCubics(
  x1: number,
  y1: number,
  rxIn: number,
  ryIn: number,
  xAxisRotationDeg: number,
  largeArc: boolean,
  sweep: boolean,
  x2: number,
  y2: number,
): Segment[] {
  if (x1 === x2 && y1 === y2) return [];
  let rx = Math.abs(rxIn);
  let ry = Math.abs(ryIn);
  if (rx === 0 || ry === 0) return [{ type: 'L', x: x2, y: y2 }];

  const phi = ((xAxisRotationDeg % 360) * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);

  // Step 1: compute (x1', y1').
  const dx2 = (x1 - x2) / 2;
  const dy2 = (y1 - y2) / 2;
  const x1p = cosPhi * dx2 + sinPhi * dy2;
  const y1p = -sinPhi * dx2 + cosPhi * dy2;

  // Correct out-of-range radii.
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
  }

  // Step 2: compute (cx', cy').
  const rx2 = rx * rx;
  const ry2 = ry * ry;
  const num = rx2 * ry2 - rx2 * y1p * y1p - ry2 * x1p * x1p;
  const den = rx2 * y1p * y1p + ry2 * x1p * x1p;
  const coefficient = (largeArc !== sweep ? 1 : -1) * Math.sqrt(Math.max(0, num / den));
  const cxp = coefficient * ((rx * y1p) / ry);
  const cyp = coefficient * (-(ry * x1p) / rx);

  // Step 3: compute (cx, cy).
  const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;

  // Step 4: start angle and sweep.
  const ux = (x1p - cxp) / rx;
  const uy = (y1p - cyp) / ry;
  const vx = (-x1p - cxp) / rx;
  const vy = (-y1p - cyp) / ry;
  const theta1 = vectorAngle(1, 0, ux, uy);
  let delta = vectorAngle(ux, uy, vx, vy);
  if (!sweep && delta > 0) delta -= 2 * Math.PI;
  else if (sweep && delta < 0) delta += 2 * Math.PI;

  const pieces = Math.max(1, Math.ceil(Math.abs(delta) / (Math.PI / 2) - 1e-9));
  const step = delta / pieces;
  const k = (4 / 3) * Math.tan(step / 4);

  const map = (ex: number, ey: number) => ({
    x: cosPhi * rx * ex - sinPhi * ry * ey + cx,
    y: sinPhi * rx * ex + cosPhi * ry * ey + cy,
  });

  const out: Segment[] = [];
  let theta = theta1;
  for (let i = 0; i < pieces; i += 1) {
    const cos0 = Math.cos(theta);
    const sin0 = Math.sin(theta);
    const thetaNext = theta + step;
    const cos1 = Math.cos(thetaNext);
    const sin1 = Math.sin(thetaNext);
    const c1 = map(cos0 - k * sin0, sin0 + k * cos0);
    const c2 = map(cos1 + k * sin1, sin1 - k * cos1);
    const end = i === pieces - 1 ? { x: x2, y: y2 } : map(cos1, sin1);
    out.push({ type: 'C', x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y, x: end.x, y: end.y });
    theta = thetaNext;
  }
  return out;
}

function vectorAngle(ux: number, uy: number, vx: number, vy: number): number {
  const dot = ux * vx + uy * vy;
  const len = Math.hypot(ux, uy) * Math.hypot(vx, vy);
  let angle = Math.acos(Math.min(1, Math.max(-1, dot / len)));
  if (ux * vy - uy * vx < 0) angle = -angle;
  return angle;
}
